export type CustomerValueModelType = "existing_customer_value" | "new_customer_value";
export type CustomerValueModelStorageType = CustomerValueModelType | "unclassified";
export type CustomerValueCriterionType = "legacy" | "quantitative" | "qualitative" | "boolean" | "calculated";
export type CustomerValueCriterionValueType =
  | "unknown"
  | "currency"
  | "percentage"
  | "integer"
  | "decimal"
  | "days"
  | "months"
  | "years"
  | "count"
  | "text_choice"
  | "boolean";
export type CustomerValueCriterionUnitType =
  | "EUR"
  | "percentage"
  | "count"
  | "days"
  | "months"
  | "years"
  | "custom"
  | "none";
export type CustomerValueScoringDirection = "higher_is_better" | "lower_is_better" | "manual_bands";
export type CustomerValueSourceStrategy =
  | "legacy_assessment"
  | "manual"
  | "crm_mapped"
  | "customer_value_process"
  | "deal_win_engine"
  | "calculated_formula";
export type CustomerValueRecordStatus = "draft" | "confirmed" | "superseded" | "archived";
export type CustomerValueScoringConfigurationStatus = "unconfigured" | "draft" | "confirmed" | "superseded";
export type CustomerValueCriterionManagementErrorCode =
  | "criterion_name_required"
  | "category_name_required"
  | "criterion_category_not_found"
  | "category_not_found"
  | "category_inactive"
  | "category_cross_model"
  | "category_cross_workspace"
  | "criterion_category_has_criteria"
  | "category_has_criteria"
  | "category_cannot_delete_confirmed"
  | "criterion_cannot_delete_confirmed"
  | "criterion_has_historical_usage"
  | "criterion_has_active_mapping"
  | "criterion_weight_total_invalid"
  | "criterion_configuration_incomplete"
  | "criterion_type_transition_not_allowed";

export interface CustomerValueCriterionCategory {
  id: string;
  modelId: string;
  name: string;
  description?: string | null;
  rank: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type CustomerValueCriterionScoringErrorCode =
  | "invalid_model_type"
  | "invalid_model_storage_type"
  | "empty_criterion_name"
  | "invalid_criterion_type"
  | "invalid_value_type"
  | "invalid_unit_type"
  | "custom_unit_required"
  | "custom_unit_not_allowed"
  | "invalid_weight"
  | "invalid_scoring_direction"
  | "invalid_source_strategy"
  | "invalid_scoring_configuration_status"
  | "criterion_model_type_mismatch"
  | "scoring_configuration_missing"
  | "scoring_band_empty"
  | "scoring_band_points_out_of_range"
  | "scoring_band_duplicate_points"
  | "scoring_band_missing_points"
  | "scoring_band_invalid_bounds"
  | "scoring_band_gap"
  | "scoring_band_overlap"
  | "scoring_band_ambiguous_boundary"
  | "scoring_band_duplicate_open_lower"
  | "scoring_band_duplicate_open_upper"
  | "qualitative_option_empty"
  | "qualitative_option_duplicate"
  | "qualitative_option_minimum_not_met"
  | "boolean_band_missing_true"
  | "boolean_band_missing_false"
  | "boolean_band_duplicate_value"
  | "missing_input"
  | "criterion_value_unmatched"
  | "calculated_formula_not_resolved"
  | "weighted_score_invalid_weight_total"
  | "weighted_score_no_active_criteria"
  | "weighted_score_missing_criterion_score"
  | "duplicate_primary_crm_mapping"
  | "invalid_crm_mapping";

export interface CustomerValueCriterionScoringError {
  code: CustomerValueCriterionScoringErrorCode;
  path: string;
  messageKey: string;
  metadata?: Record<string, string | number | boolean | null>;
}

export interface CustomerValueCriterionFormulaOperand {
  source: "criterion" | "constant";
  criterionId?: string;
  value?: number;
}

export interface CustomerValueCriterionFormulaContract {
  type: "arithmetic";
  operator: "add" | "subtract" | "multiply" | "divide";
  left: CustomerValueCriterionFormulaOperand;
  right: CustomerValueCriterionFormulaOperand;
}

export interface CustomerValueCriterionDefinition {
  id: string;
  modelId: string;
  modelType: CustomerValueModelType;
  categoryId?: string | null;
  name: string;
  description?: string;
  criterionType: CustomerValueCriterionType;
  valueType: CustomerValueCriterionValueType;
  unitType: CustomerValueCriterionUnitType;
  customUnit?: string | null;
  weight: number;
  scoringDirection: CustomerValueScoringDirection;
  required: boolean;
  active: boolean;
  rank: number;
  sourceStrategy: CustomerValueSourceStrategy;
  scoringConfigurationStatus: CustomerValueScoringConfigurationStatus;
  formula?: CustomerValueCriterionFormulaContract | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerValueCriterionScoringBand {
  id: string;
  criterionId: string;
  version: number;
  points: number | null;
  minValue?: number | null;
  maxValue?: number | null;
  minInclusive: boolean;
  maxInclusive: boolean;
  qualitativeValue?: string | null;
  qualitativeOptionId?: string | null;
  qualitativeLabelDe?: string | null;
  qualitativeLabelEn?: string | null;
  booleanValue?: boolean | null;
  rank: number;
  status: CustomerValueRecordStatus;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerValueCriterionCrmMappingContract {
  id: string;
  workspaceId: string;
  modelId: string;
  criterionId: string;
  crmProvider: "hubspot" | "salesforce" | "pipedrive" | "custom";
  crmObjectType: "deal" | "company" | "contact" | "account" | "opportunity" | "custom";
  crmFieldName: string;
  transformationType: "none" | "currency" | "percentage" | "lookup" | "formula";
  sourceCurrency?: string | null;
  targetCurrency?: string | null;
  active: boolean;
  syncDirection: "read_only_from_crm" | "write_back_to_crm" | "bidirectional_blocked";
  primary: boolean;
}

export interface CustomerValueScoreSegment {
  id: string;
  name: string;
  minScore: number;
  maxScore: number;
  minInclusive: boolean;
  maxInclusive: boolean;
}

export interface CriterionScoringValidationResult {
  ok: boolean;
  errors: CustomerValueCriterionScoringError[];
}

export interface CriterionScoringBandSetValidationResult {
  valid: boolean;
  errors: CustomerValueCriterionScoringError[];
  warnings: CustomerValueCriterionScoringError[];
  normalizedBands?: readonly CustomerValueCriterionScoringBand[];
  confirmationAllowed: boolean;
  previewEligible: boolean;
}

export interface CriterionScoreResolution {
  ok: boolean;
  points?: number;
  normalizedScore?: number;
  band?: CustomerValueCriterionScoringBand;
  errors: CustomerValueCriterionScoringError[];
}

export interface WeightedCustomerValueScoreInput {
  criteria: readonly CustomerValueCriterionDefinition[];
  bandsByCriterionId: Readonly<Record<string, readonly CustomerValueCriterionScoringBand[]>>;
  valuesByCriterionId: Readonly<Record<string, unknown>>;
}

export interface WeightedCustomerValueScoreResolution {
  ok: boolean;
  finalScore?: number;
  contributions: readonly {
    criterionId: string;
    points: number;
    normalizedScore: number;
    weight: number;
    weightedContribution: number;
  }[];
  errors: CustomerValueCriterionScoringError[];
}

export const CUSTOMER_VALUE_SCORE_OUTPUT_DECIMALS = 2;
export const CUSTOMER_VALUE_SCORING_ENGINE_VERSION = "customer_value_scoring_v1";
export const CUSTOMER_VALUE_SCORE_SCALE = {
  min: 0,
  max: 100,
  criterionPointMin: 1,
  criterionPointMax: 10,
  weightingUnit: "percent",
  rounding: `round half away from zero to ${CUSTOMER_VALUE_SCORE_OUTPUT_DECIMALS} decimals at final output`
} as const;

export type CustomerValueScoringCriterionReasonCode =
  | "missing_input"
  | "missing_scoring_logic"
  | "missing_weight"
  | "invalid_weight"
  | "ambiguous_scoring_band"
  | "no_matching_scoring_band"
  | "calculation_error";

export type CustomerValueScoringGlobalReasonCode =
  | "no_active_criteria"
  | "invalid_weight_sum"
  | "invalid_segment_configuration"
  | "no_matching_segment"
  | "ambiguous_segment"
  | "invalid_model"
  | "technical_error";

export type CustomerValueScoringBlockingReasonCode =
  | CustomerValueScoringCriterionReasonCode
  | CustomerValueScoringGlobalReasonCode;

export interface CustomerValueConfirmedCriterionWeight {
  criterionId: string;
  weight: number | null;
  status?: CustomerValueRecordStatus;
  version?: number | null;
}

export interface CustomerValueScoringModelReference {
  workspaceId: string;
  modelId: string;
  modelType: CustomerValueModelStorageType;
  status?: string;
}

export interface CustomerValueScoreInput {
  model: CustomerValueScoringModelReference;
  modelVersion: number | null;
  criteria: readonly CustomerValueCriterionDefinition[];
  criterionValues: Readonly<Record<string, unknown>>;
  confirmedScoringBands: Readonly<Record<string, readonly CustomerValueCriterionScoringBand[]>> | readonly CustomerValueCriterionScoringBand[];
  confirmedWeights?: Readonly<Record<string, CustomerValueConfirmedCriterionWeight>>;
  segments: readonly CustomerValueScoreSegment[];
  weightingVersion?: number | null;
  calculatedAt: string;
}

export type CustomerValueCriterionResultStatus = "computed" | "blocked" | "ignored_inactive";

export interface CustomerValueCriterionScoreResult {
  criterionId: string;
  rawValue: unknown;
  normalizedValue: string | number | boolean | null;
  scoringBandId: string | null;
  scoringBandVersion: number | null;
  points: number | null;
  weight: number | null;
  weightedContribution: number | null;
  status: CustomerValueCriterionResultStatus;
  reasonCode: CustomerValueScoringCriterionReasonCode | null;
}

export interface CustomerValueScoringReadiness {
  ready: boolean;
  blockingReasons: readonly CustomerValueScoringBlockingReasonCode[];
  warnings: readonly string[];
  activeCriterionCount: number;
  computableCriterionCount: number;
  missingInputCriterionIds: readonly string[];
  missingScoringLogicCriterionIds: readonly string[];
  missingWeightCriterionIds: readonly string[];
  invalidWeightCriterionIds: readonly string[];
  ambiguousCriterionIds: readonly string[];
  unresolvedCriterionIds: readonly string[];
  segmentConfigurationValid: boolean;
  modelVersion: number | null;
  weightingVersion: number | null;
  scoringVersions: Readonly<Record<string, number>>;
}

export interface CustomerValueScoreSnapshot {
  workspaceId: string;
  modelId: string;
  modelVersion: number | null;
  engineVersion: typeof CUSTOMER_VALUE_SCORING_ENGINE_VERSION;
  calculatedAt: string;
  criterionValues: Readonly<Record<string, unknown>>;
  criterionIds: readonly string[];
  scoringBandIds: readonly string[];
  scoringBandVersions: Readonly<Record<string, number>>;
  points: Readonly<Record<string, number | null>>;
  weights: Readonly<Record<string, number | null>>;
  weightingVersion: number | null;
  weightedContributions: Readonly<Record<string, number | null>>;
  segmentDefinitionIds: readonly string[];
  segmentId: string | null;
  rawScore: number | null;
  roundedScore: number | null;
  scoreScale: typeof CUSTOMER_VALUE_SCORE_SCALE;
}

export interface CustomerValueScoreResult {
  success: boolean;
  readiness: CustomerValueScoringReadiness;
  modelId: string;
  modelVersion: number | null;
  engineVersion: typeof CUSTOMER_VALUE_SCORING_ENGINE_VERSION;
  rawScore: number | null;
  roundedScore: number | null;
  scoreScale: typeof CUSTOMER_VALUE_SCORE_SCALE;
  segmentId: string | null;
  segment?: CustomerValueScoreSegment;
  criterionResults: readonly CustomerValueCriterionScoreResult[];
  blockingReasons: readonly CustomerValueScoringBlockingReasonCode[];
  warnings: readonly string[];
  calculatedAt: string;
  inputSnapshot: CustomerValueScoreSnapshot;
}

export type CriterionBandResolution =
  | { status: "matched"; bandId: string; bandVersion: number; points: number; band: CustomerValueCriterionScoringBand; normalizedValue: string | number | boolean }
  | { status: "no_match"; reasonCode: "no_matching_scoring_band"; normalizedValue: string | number | boolean | null }
  | { status: "ambiguous"; reasonCode: "ambiguous_scoring_band"; bandIds: readonly string[]; normalizedValue: string | number | boolean | null }
  | { status: "missing_input"; reasonCode: "missing_input"; normalizedValue: null }
  | { status: "missing_scoring_logic"; reasonCode: "missing_scoring_logic"; normalizedValue: string | number | boolean | null };

export type CustomerValueSegmentResolution =
  | { status: "matched"; segmentId: string; segment: CustomerValueScoreSegment }
  | { status: "no_match"; reasonCode: "no_matching_segment" }
  | { status: "ambiguous"; reasonCode: "ambiguous_segment"; segmentIds: readonly string[] }
  | { status: "invalid_configuration"; reasonCode: "invalid_segment_configuration"; segmentIds: readonly string[] };

export interface CustomerValueCriteriaWeightValidation {
  ok: boolean;
  totalWeight: number;
  errors: CustomerValueCriterionScoringError[];
}

export type CustomerValueScoringConfigurationDraftStatus =
  | "incomplete_underweighted"
  | "complete"
  | "incomplete_overweighted";

const MODEL_TYPES: readonly CustomerValueModelType[] = ["existing_customer_value", "new_customer_value"];
const MODEL_STORAGE_TYPES: readonly CustomerValueModelStorageType[] = ["existing_customer_value", "new_customer_value", "unclassified"];
const CRITERION_TYPES: readonly CustomerValueCriterionType[] = ["legacy", "quantitative", "qualitative", "boolean", "calculated"];
const VALUE_TYPES: readonly CustomerValueCriterionValueType[] = [
  "unknown",
  "currency",
  "percentage",
  "integer",
  "decimal",
  "days",
  "months",
  "years",
  "count",
  "text_choice",
  "boolean"
];
const UNIT_TYPES: readonly CustomerValueCriterionUnitType[] = ["EUR", "percentage", "count", "days", "months", "years", "custom", "none"];
const SCORING_DIRECTIONS: readonly CustomerValueScoringDirection[] = ["higher_is_better", "lower_is_better", "manual_bands"];
const SOURCE_STRATEGIES: readonly CustomerValueSourceStrategy[] = [
  "legacy_assessment",
  "manual",
  "crm_mapped",
  "customer_value_process",
  "deal_win_engine",
  "calculated_formula"
];
const SCORING_CONFIGURATION_STATUSES: readonly CustomerValueScoringConfigurationStatus[] = ["unconfigured", "draft", "confirmed", "superseded"];
const FULL_POINT_SCALE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
const EPSILON = 1e-9;

function scoringError(
  code: CustomerValueCriterionScoringErrorCode,
  path: string,
  metadata?: CustomerValueCriterionScoringError["metadata"]
): CustomerValueCriterionScoringError {
  return {
    code,
    path,
    messageKey: `customerValue.criterionScoring.${code}`,
    metadata
  };
}

function includesValue<T extends string>(items: readonly T[], value: unknown): value is T {
  return typeof value === "string" && items.includes(value as T);
}

function activeBands(bands: readonly CustomerValueCriterionScoringBand[]): CustomerValueCriterionScoringBand[] {
  return bands.filter((band) => band.active && band.status !== "archived").sort(compareBands);
}

function activeScoringConfigurationBands(bands: readonly CustomerValueCriterionScoringBand[]): CustomerValueCriterionScoringBand[] {
  return bands.filter((band) => band.active && (band.status === "draft" || band.status === "confirmed"));
}

function isConfirmedLegacyAssessmentWithoutStructuredBands(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[],
  modelStatus: string | null | undefined
): boolean {
  return modelStatus === "confirmed" &&
    criterion.active &&
    criterion.criterionType === "legacy" &&
    criterion.sourceStrategy === "legacy_assessment" &&
    activeScoringConfigurationBands(bands).length === 0;
}

function resolveLegacyAssessmentDirectBand(
  criterion: CustomerValueCriterionDefinition,
  rawValue: unknown
): CriterionBandResolution {
  const numericInput = parseNumericCriterionInput(rawValue);
  if (!numericInput.ok && numericInput.missing) return { status: "missing_input", reasonCode: "missing_input", normalizedValue: null };
  if (!numericInput.ok || !Number.isInteger(numericInput.value) || !isValidCriterionPoints(numericInput.value)) {
    return { status: "no_match", reasonCode: "no_matching_scoring_band", normalizedValue: numericInput.ok ? numericInput.value : null };
  }
  const points = numericInput.value;
  const band: CustomerValueCriterionScoringBand = {
    id: `legacy-assessment-direct:${criterion.id}:${points}`,
    criterionId: criterion.id,
    version: 0,
    points,
    minValue: points,
    maxValue: points,
    minInclusive: true,
    maxInclusive: true,
    qualitativeValue: null,
    qualitativeOptionId: null,
    qualitativeLabelDe: null,
    qualitativeLabelEn: null,
    booleanValue: null,
    rank: points,
    status: "confirmed",
    active: true
  };
  return {
    status: "matched",
    bandId: band.id,
    bandVersion: band.version,
    points,
    band,
    normalizedValue: points
  };
}

function compareBands(left: CustomerValueCriterionScoringBand, right: CustomerValueCriterionScoringBand): number {
  if (left.minValue === null || left.minValue === undefined) return right.minValue === null || right.minValue === undefined ? left.rank - right.rank : -1;
  if (right.minValue === null || right.minValue === undefined) return 1;
  if (Math.abs(left.minValue - right.minValue) > EPSILON) return left.minValue - right.minValue;
  if (left.maxValue === null || left.maxValue === undefined) return right.maxValue === null || right.maxValue === undefined ? left.rank - right.rank : 1;
  if (right.maxValue === null || right.maxValue === undefined) return -1;
  if (Math.abs(left.maxValue - right.maxValue) > EPSILON) return left.maxValue - right.maxValue;
  return left.rank - right.rank;
}

function isIntegerLikeValueType(valueType: CustomerValueCriterionValueType): boolean {
  return ["integer", "days", "months", "years", "count"].includes(valueType);
}

function numericBandContainsValue(band: CustomerValueCriterionScoringBand, rawValue: number): boolean {
  if (band.minValue !== null && band.minValue !== undefined) {
    if (rawValue < band.minValue || (Math.abs(rawValue - band.minValue) <= EPSILON && !band.minInclusive)) return false;
  }
  if (band.maxValue !== null && band.maxValue !== undefined) {
    if (rawValue > band.maxValue || (Math.abs(rawValue - band.maxValue) <= EPSILON && !band.maxInclusive)) return false;
  }
  return true;
}

function parseNumericCriterionInput(rawValue: unknown): { ok: true; value: number } | { ok: false; missing: boolean } {
  if (typeof rawValue === "string" && rawValue.trim() === "") return { ok: false, missing: true };
  const numericValue = typeof rawValue === "number" ? rawValue : Number(rawValue);
  return Number.isFinite(numericValue) ? { ok: true, value: numericValue } : { ok: false, missing: false };
}

function parseBooleanCriterionInput(rawValue: unknown): { ok: true; value: boolean } | { ok: false; missing: boolean } {
  if (rawValue === true || rawValue === false) return { ok: true, value: rawValue };
  if (typeof rawValue === "string") {
    const normalized = rawValue.trim().toLowerCase();
    if (!normalized) return { ok: false, missing: true };
    if (normalized === "true") return { ok: true, value: true };
    if (normalized === "false") return { ok: true, value: false };
  }
  return { ok: false, missing: false };
}

function isValidCriterionPoints(points: number | null | undefined): points is number {
  return typeof points === "number" && Number.isInteger(points) && points >= 1 && points <= 10;
}

function sameBoundary(left: number, right: number): boolean {
  return Math.abs(left - right) <= EPSILON;
}

export function isCustomerValueModelType(value: unknown): value is CustomerValueModelType {
  return includesValue(MODEL_TYPES, value);
}

export function isCustomerValueModelStorageType(value: unknown): value is CustomerValueModelStorageType {
  return includesValue(MODEL_STORAGE_TYPES, value);
}

export function validateCustomerValueModelType(value: unknown): CriterionScoringValidationResult {
  return isCustomerValueModelType(value)
    ? { ok: true, errors: [] }
    : { ok: false, errors: [scoringError("invalid_model_type", "modelType")] };
}

export function validateCustomerValueCriterion(
  criterion: CustomerValueCriterionDefinition,
  expectedModelType?: CustomerValueModelType
): CriterionScoringValidationResult {
  const errors: CustomerValueCriterionScoringError[] = [];
  if (!isCustomerValueModelType(criterion.modelType)) errors.push(scoringError("invalid_model_type", "modelType"));
  if (expectedModelType && criterion.modelType !== expectedModelType) {
    errors.push(scoringError("criterion_model_type_mismatch", "modelType", { expectedModelType, actualModelType: criterion.modelType }));
  }
  if (!criterion.name.trim()) errors.push(scoringError("empty_criterion_name", "name"));
  if (!includesValue(CRITERION_TYPES, criterion.criterionType)) errors.push(scoringError("invalid_criterion_type", "criterionType"));
  if (!includesValue(VALUE_TYPES, criterion.valueType)) errors.push(scoringError("invalid_value_type", "valueType"));
  if (!includesValue(UNIT_TYPES, criterion.unitType)) errors.push(scoringError("invalid_unit_type", "unitType"));
  if (criterion.unitType === "custom" && !criterion.customUnit?.trim()) errors.push(scoringError("custom_unit_required", "customUnit"));
  if (criterion.unitType !== "custom" && criterion.customUnit?.trim()) errors.push(scoringError("custom_unit_not_allowed", "customUnit"));
  if (!Number.isFinite(criterion.weight) || criterion.weight < 0 || criterion.weight > 100) {
    errors.push(scoringError("invalid_weight", "weight", { weight: criterion.weight }));
  }
  if (!includesValue(SCORING_DIRECTIONS, criterion.scoringDirection)) errors.push(scoringError("invalid_scoring_direction", "scoringDirection"));
  if (!includesValue(SOURCE_STRATEGIES, criterion.sourceStrategy)) errors.push(scoringError("invalid_source_strategy", "sourceStrategy"));
  if (!includesValue(SCORING_CONFIGURATION_STATUSES, criterion.scoringConfigurationStatus)) {
    errors.push(scoringError("invalid_scoring_configuration_status", "scoringConfigurationStatus"));
  }
  return { ok: errors.length === 0, errors };
}

export function validateCustomerValueModelCompatibility(
  modelType: CustomerValueModelType,
  criteria: readonly CustomerValueCriterionDefinition[]
): CriterionScoringValidationResult {
  const errors = criteria.flatMap((criterion, index) =>
    criterion.modelType === modelType
      ? []
      : [scoringError("criterion_model_type_mismatch", `criteria.${index}.modelType`, { criterionId: criterion.id, expectedModelType: modelType })]
  );
  return { ok: errors.length === 0, errors };
}

export function validateFullTenPointScale(bands: readonly CustomerValueCriterionScoringBand[]): CriterionScoringValidationResult {
  const presentPoints = new Set(activeBands(bands).map((band) => band.points).filter((points): points is number => typeof points === "number"));
  const errors = FULL_POINT_SCALE.filter((point) => !presentPoints.has(point)).map((point) =>
    scoringError("scoring_band_missing_points", "bands.points", { point })
  );
  return { ok: errors.length === 0, errors };
}

function validateBandPoints(bands: readonly CustomerValueCriterionScoringBand[]): CustomerValueCriterionScoringError[] {
  const errors: CustomerValueCriterionScoringError[] = [];
  const seenPoints = new Set<number>();
  for (const [index, band] of activeBands(bands).entries()) {
    if (band.points === null || band.points === undefined) {
      errors.push(scoringError("scoring_band_missing_points", `bands.${index}.points`, { bandId: band.id }));
      continue;
    }
    if (!Number.isInteger(band.points) || band.points < 1 || band.points > 10) {
      errors.push(scoringError("scoring_band_points_out_of_range", `bands.${index}.points`, { points: band.points }));
      continue;
    }
    if (seenPoints.has(band.points)) {
      errors.push(scoringError("scoring_band_duplicate_points", `bands.${index}.points`, { points: band.points }));
    }
    seenPoints.add(band.points);
  }
  return errors;
}

export function validateQuantitativeScoringBands(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[]
): CriterionScoringValidationResult {
  const sortedBands = activeBands(bands);
  const errors: CustomerValueCriterionScoringError[] = [];
  if (sortedBands.length === 0) errors.push(scoringError("scoring_band_empty", "bands"));
  errors.push(...validateBandPoints(sortedBands), ...validateFullTenPointScale(sortedBands).errors);

  const openLowerCount = sortedBands.filter((band) => band.minValue === null || band.minValue === undefined).length;
  const openUpperCount = sortedBands.filter((band) => band.maxValue === null || band.maxValue === undefined).length;
  if (openLowerCount > 1) errors.push(scoringError("scoring_band_duplicate_open_lower", "bands.minValue"));
  if (openUpperCount > 1) errors.push(scoringError("scoring_band_duplicate_open_upper", "bands.maxValue"));
  const seenRanges = new Set<string>();

  for (const [index, band] of sortedBands.entries()) {
    const minValue = band.minValue;
    const maxValue = band.maxValue;
    const hasMin = minValue !== null && minValue !== undefined;
    const hasMax = maxValue !== null && maxValue !== undefined;
    if ((hasMin && !Number.isFinite(minValue)) || (hasMax && !Number.isFinite(maxValue))) {
      errors.push(scoringError("scoring_band_invalid_bounds", `bands.${index}`, { bandId: band.id }));
    }
    if (hasMin && hasMax && minValue > maxValue) {
      errors.push(scoringError("scoring_band_invalid_bounds", `bands.${index}`, { bandId: band.id }));
    }
    const rangeKey = `${hasMin ? minValue : "open"}:${band.minInclusive ? "closed" : "open"}-${hasMax ? maxValue : "open"}:${band.maxInclusive ? "closed" : "open"}`;
    if (seenRanges.has(rangeKey)) {
      errors.push(scoringError("scoring_band_overlap", `bands.${index}`, { bandId: band.id }));
    }
    seenRanges.add(rangeKey);
    if (index > 0) {
      const previous = sortedBands[index - 1];
      if (previous.maxValue === null || previous.maxValue === undefined || band.minValue === null || band.minValue === undefined) continue;
      if (band.minValue < previous.maxValue && !sameBoundary(band.minValue, previous.maxValue)) {
        errors.push(scoringError("scoring_band_overlap", `bands.${index}`, { previousBandId: previous.id, currentBandId: band.id }));
        continue;
      }
      if (sameBoundary(band.minValue, previous.maxValue)) {
        if (previous.maxInclusive && band.minInclusive) {
          errors.push(scoringError("scoring_band_ambiguous_boundary", `bands.${index}`, { previousBandId: previous.id, currentBandId: band.id }));
        }
        if (!previous.maxInclusive && !band.minInclusive) {
          errors.push(scoringError("scoring_band_gap", `bands.${index}`, { previousBandId: previous.id, currentBandId: band.id }));
        }
        continue;
      }
      const integerAdjacent =
        isIntegerLikeValueType(criterion.valueType) && previous.maxInclusive && band.minInclusive && sameBoundary(band.minValue, previous.maxValue + 1);
      if (!integerAdjacent) {
        errors.push(scoringError("scoring_band_gap", `bands.${index}`, { previousBandId: previous.id, currentBandId: band.id }));
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateDraftScoringBandsForPersistence(
  _criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[]
): CriterionScoringValidationResult {
  const sortedBands = activeBands(bands);
  const errors: CustomerValueCriterionScoringError[] = [];
  if (sortedBands.length === 0) errors.push(scoringError("scoring_band_empty", "bands"));
  errors.push(...validateBandPoints(sortedBands));
  const seenRanges = new Set<string>();
  for (const [index, band] of sortedBands.entries()) {
    const minValue = band.minValue;
    const maxValue = band.maxValue;
    const hasMin = minValue !== null && minValue !== undefined;
    const hasMax = maxValue !== null && maxValue !== undefined;
    if ((hasMin && !Number.isFinite(minValue)) || (hasMax && !Number.isFinite(maxValue))) {
      errors.push(scoringError("scoring_band_invalid_bounds", `bands.${index}`, { bandId: band.id }));
    }
    if (hasMin && hasMax && minValue > maxValue) {
      errors.push(scoringError("scoring_band_invalid_bounds", `bands.${index}`, { bandId: band.id }));
    }
    const rangeKey = `${hasMin ? minValue : "open"}:${band.minInclusive ? "closed" : "open"}-${hasMax ? maxValue : "open"}:${band.maxInclusive ? "closed" : "open"}`;
    if (seenRanges.has(rangeKey)) errors.push(scoringError("scoring_band_overlap", `bands.${index}`, { bandId: band.id }));
    seenRanges.add(rangeKey);
  }
  return { ok: errors.length === 0, errors };
}

export function validateCriterionScoringBandSet(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[]
): CriterionScoringBandSetValidationResult {
  const visibleBands = activeBands(bands);
  let result: CriterionScoringValidationResult;
  if (!criterion.active) {
    return {
      valid: true,
      errors: [],
      warnings: [],
      normalizedBands: visibleBands,
      confirmationAllowed: false,
      previewEligible: false
    };
  }
  if (criterion.criterionType === "quantitative" || (criterion.criterionType === "legacy" && visibleBands.some((band) => band.minValue !== null || band.maxValue !== null))) {
    result = validateQuantitativeScoringBands({ ...criterion, criterionType: "quantitative" }, visibleBands);
  } else if (criterion.criterionType === "qualitative" || (criterion.criterionType === "legacy" && visibleBands.some((band) => band.qualitativeOptionId ?? band.qualitativeValue))) {
    result = validateQualitativeScoringBands({ ...criterion, criterionType: "qualitative" }, visibleBands);
  } else if (criterion.criterionType === "boolean" || (criterion.criterionType === "legacy" && visibleBands.some((band) => band.booleanValue !== null && band.booleanValue !== undefined))) {
    result = validateBooleanScoringBands({ ...criterion, criterionType: "boolean" }, visibleBands);
  } else {
    result = { ok: false, errors: [scoringError("scoring_configuration_missing", "bands", { criterionId: criterion.id })] };
  }
  const confirmedBands = visibleBands.filter((band) => band.status === "confirmed");
  return {
    valid: result.ok,
    errors: result.errors,
    warnings: [],
    normalizedBands: result.ok ? visibleBands : undefined,
    confirmationAllowed: result.ok,
    previewEligible: result.ok && criterion.scoringConfigurationStatus === "confirmed" && confirmedBands.length === visibleBands.length && confirmedBands.length > 0
  };
}

export function validateQualitativeScoringBands(
  _criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[]
): CriterionScoringValidationResult {
  const errors: CustomerValueCriterionScoringError[] = [];
  const sortedBands = activeBands(bands);
  if (sortedBands.length === 0) errors.push(scoringError("scoring_band_empty", "bands"));
  if (sortedBands.length === 1) errors.push(scoringError("qualitative_option_minimum_not_met", "bands"));
  errors.push(...validateBandPoints(sortedBands));
  const seenValues = new Set<string>();
  for (const [index, band] of sortedBands.entries()) {
    const optionId = (band.qualitativeOptionId ?? band.qualitativeValue)?.trim() ?? "";
    if (!optionId) errors.push(scoringError("qualitative_option_empty", `bands.${index}.qualitativeOptionId`, { bandId: band.id }));
    if (optionId && seenValues.has(optionId)) errors.push(scoringError("qualitative_option_duplicate", `bands.${index}.qualitativeOptionId`, { optionId }));
    seenValues.add(optionId);
  }
  return { ok: errors.length === 0, errors };
}

export function validateBooleanScoringBands(
  _criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[]
): CriterionScoringValidationResult {
  const errors: CustomerValueCriterionScoringError[] = [];
  const sortedBands = activeBands(bands);
  if (sortedBands.length === 0) errors.push(scoringError("scoring_band_empty", "bands"));
  errors.push(...validateBandPoints(sortedBands));
  const trueBands = sortedBands.filter((band) => band.booleanValue === true);
  const falseBands = sortedBands.filter((band) => band.booleanValue === false);
  if (trueBands.length === 0) errors.push(scoringError("boolean_band_missing_true", "bands.booleanValue"));
  if (falseBands.length === 0) errors.push(scoringError("boolean_band_missing_false", "bands.booleanValue"));
  if (trueBands.length > 1 || falseBands.length > 1) errors.push(scoringError("boolean_band_duplicate_value", "bands.booleanValue"));
  return { ok: errors.length === 0, errors };
}

export function validateCalculatedCriterionContract(criterion: CustomerValueCriterionDefinition): CriterionScoringValidationResult {
  return criterion.criterionType === "calculated" && !criterion.formula
    ? { ok: false, errors: [scoringError("calculated_formula_not_resolved", "formula")] }
    : { ok: true, errors: [] };
}

export function normalizeCriterionPoints(points: number): number {
  return ((points - 1) / 9) * 100;
}

export function roundCustomerValueScoreForOutput(score: number): number {
  return Math.round(score * 10 ** CUSTOMER_VALUE_SCORE_OUTPUT_DECIMALS) / 10 ** CUSTOMER_VALUE_SCORE_OUTPUT_DECIMALS;
}

export function resolveCustomerValueSegmentForScore(
  unroundedScore: number,
  segments: readonly CustomerValueScoreSegment[]
): CustomerValueScoreSegment | undefined {
  const resolution = resolveCustomerValueSegment(unroundedScore, segments);
  return resolution.status === "matched" ? resolution.segment : undefined;
}

export function resolveCustomerValueSegment(
  score: number,
  confirmedSegments: readonly CustomerValueScoreSegment[]
): CustomerValueSegmentResolution {
  const invalidSegmentIds = confirmedSegments
    .filter((segment) =>
      !Number.isFinite(segment.minScore) ||
      !Number.isFinite(segment.maxScore) ||
      segment.minScore > segment.maxScore
    )
    .map((segment) => segment.id);
  if (invalidSegmentIds.length > 0) {
    return { status: "invalid_configuration", reasonCode: "invalid_segment_configuration", segmentIds: invalidSegmentIds };
  }
  const matches = confirmedSegments.filter((segment) => {
    const minMatches = score > segment.minScore || (Math.abs(score - segment.minScore) <= EPSILON && segment.minInclusive);
    const maxMatches = score < segment.maxScore || (Math.abs(score - segment.maxScore) <= EPSILON && segment.maxInclusive);
    return minMatches && maxMatches;
  });
  if (matches.length === 1) return { status: "matched", segmentId: matches[0].id, segment: { ...matches[0] } };
  if (matches.length > 1) return { status: "ambiguous", reasonCode: "ambiguous_segment", segmentIds: matches.map((segment) => segment.id) };
  return { status: "no_match", reasonCode: "no_matching_segment" };
}

function isNumericCriterion(criterion: CustomerValueCriterionDefinition, bands: readonly CustomerValueCriterionScoringBand[]): boolean {
  return criterion.criterionType === "quantitative" ||
    (criterion.criterionType === "legacy" && bands.some((candidate) => candidate.minValue !== null || candidate.maxValue !== null));
}

function isQualitativeCriterion(criterion: CustomerValueCriterionDefinition, bands: readonly CustomerValueCriterionScoringBand[]): boolean {
  return criterion.criterionType === "qualitative" ||
    (criterion.criterionType === "legacy" && bands.some((candidate) => candidate.qualitativeOptionId ?? candidate.qualitativeValue));
}

function isBooleanCriterion(criterion: CustomerValueCriterionDefinition, bands: readonly CustomerValueCriterionScoringBand[]): boolean {
  return criterion.criterionType === "boolean" ||
    (criterion.criterionType === "legacy" && bands.some((candidate) => candidate.booleanValue !== null && candidate.booleanValue !== undefined));
}

export function resolveCriterionBand(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[],
  rawValue: unknown,
  context: { modelStatus?: string | null } = {}
): CriterionBandResolution {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return { status: "missing_input", reasonCode: "missing_input", normalizedValue: null };
  }
  if (isConfirmedLegacyAssessmentWithoutStructuredBands(criterion, bands, context.modelStatus)) {
    return resolveLegacyAssessmentDirectBand(criterion, rawValue);
  }
  if (criterion.scoringConfigurationStatus !== "confirmed" || criterion.criterionType === "calculated") {
    return { status: "missing_scoring_logic", reasonCode: "missing_scoring_logic", normalizedValue: null };
  }
  const sortedBands = activeBands(bands.filter((band) => band.status === "confirmed"));
  if (sortedBands.length === 0) {
    return { status: "missing_scoring_logic", reasonCode: "missing_scoring_logic", normalizedValue: null };
  }

  let normalizedValue: string | number | boolean | null = null;
  let matches: CustomerValueCriterionScoringBand[] = [];
  if (isNumericCriterion(criterion, sortedBands)) {
    const numericInput = parseNumericCriterionInput(rawValue);
    if (!numericInput.ok && numericInput.missing) return { status: "missing_input", reasonCode: "missing_input", normalizedValue: null };
    if (!numericInput.ok) return { status: "no_match", reasonCode: "no_matching_scoring_band", normalizedValue: null };
    normalizedValue = numericInput.value;
    matches = sortedBands.filter((candidate) => numericBandContainsValue(candidate, numericInput.value));
  } else if (isQualitativeCriterion(criterion, sortedBands)) {
    normalizedValue = typeof rawValue === "string" ? rawValue : String(rawValue);
    matches = sortedBands.filter((candidate) => (candidate.qualitativeOptionId ?? candidate.qualitativeValue) === rawValue);
  } else if (isBooleanCriterion(criterion, sortedBands)) {
    const booleanInput = parseBooleanCriterionInput(rawValue);
    if (!booleanInput.ok && booleanInput.missing) return { status: "missing_input", reasonCode: "missing_input", normalizedValue: null };
    if (!booleanInput.ok) return { status: "no_match", reasonCode: "no_matching_scoring_band", normalizedValue: null };
    normalizedValue = booleanInput.value;
    matches = sortedBands.filter((candidate) => candidate.booleanValue === booleanInput.value);
  }

  if (matches.length > 1) {
    return {
      status: "ambiguous",
      reasonCode: "ambiguous_scoring_band",
      bandIds: matches.map((band) => band.id),
      normalizedValue
    };
  }

  const confirmedBandSet = validateCriterionScoringBandSet(criterion, sortedBands);
  if (!confirmedBandSet.previewEligible) {
    return { status: "missing_scoring_logic", reasonCode: "missing_scoring_logic", normalizedValue };
  }

  if (matches.length === 0) {
    return { status: "no_match", reasonCode: "no_matching_scoring_band", normalizedValue };
  }

  const [band] = matches;
  if (!isValidCriterionPoints(band.points)) {
    return { status: "missing_scoring_logic", reasonCode: "missing_scoring_logic", normalizedValue };
  }
  return {
    status: "matched",
    bandId: band.id,
    bandVersion: band.version,
    points: band.points,
    band,
    normalizedValue: normalizedValue ?? rawValue as string | number | boolean
  };
}

function groupScoringBandsByCriterionId(
  bands: CustomerValueScoreInput["confirmedScoringBands"]
): Readonly<Record<string, readonly CustomerValueCriterionScoringBand[]>> {
  if (Array.isArray(bands)) {
    const bandList = bands as readonly CustomerValueCriterionScoringBand[];
    return bandList.reduce<Record<string, CustomerValueCriterionScoringBand[]>>((accumulator, band) => {
      accumulator[band.criterionId] = [...(accumulator[band.criterionId] ?? []), band];
      return accumulator;
    }, {});
  }
  return bands as Readonly<Record<string, readonly CustomerValueCriterionScoringBand[]>>;
}

function confirmedWeightForCriterion(
  criterion: CustomerValueCriterionDefinition,
  confirmedWeights: CustomerValueScoreInput["confirmedWeights"]
): { weight: number | null; version: number | null; missing: boolean; invalid: boolean } {
  if (!confirmedWeights) {
    return {
      weight: criterion.weight,
      version: null,
      missing: false,
      invalid: !Number.isFinite(criterion.weight) || criterion.weight < 0 || criterion.weight > 100
    };
  }
  const confirmedWeight = confirmedWeights[criterion.id];
  if (!confirmedWeight || confirmedWeight.status !== "confirmed" || confirmedWeight.weight === null || confirmedWeight.weight === undefined) {
    return { weight: null, version: confirmedWeight?.version ?? null, missing: true, invalid: false };
  }
  return {
    weight: confirmedWeight.weight,
    version: confirmedWeight.version ?? null,
    missing: false,
    invalid: !Number.isFinite(confirmedWeight.weight) || confirmedWeight.weight < 0 || confirmedWeight.weight > 100
  };
}

function uniqueReasonCodes<T extends string>(codes: readonly T[]): T[] {
  return Array.from(new Set(codes));
}

export function deriveScoringReadiness(input: {
  criterionResults: readonly CustomerValueCriterionScoreResult[];
  globalBlockingReasons: readonly CustomerValueScoringGlobalReasonCode[];
  segmentConfigurationValid: boolean;
  modelVersion: number | null;
  weightingVersion: number | null;
}): CustomerValueScoringReadiness {
  const activeResults = input.criterionResults.filter((result) => result.status !== "ignored_inactive");
  const missingInputCriterionIds = activeResults.filter((result) => result.reasonCode === "missing_input").map((result) => result.criterionId);
  const missingScoringLogicCriterionIds = activeResults.filter((result) => result.reasonCode === "missing_scoring_logic").map((result) => result.criterionId);
  const missingWeightCriterionIds = activeResults.filter((result) => result.reasonCode === "missing_weight").map((result) => result.criterionId);
  const invalidWeightCriterionIds = activeResults.filter((result) => result.reasonCode === "invalid_weight").map((result) => result.criterionId);
  const ambiguousCriterionIds = activeResults.filter((result) => result.reasonCode === "ambiguous_scoring_band").map((result) => result.criterionId);
  const unresolvedCriterionIds = activeResults
    .filter((result) => result.reasonCode === "no_matching_scoring_band" || result.reasonCode === "calculation_error")
    .map((result) => result.criterionId);
  const scoringVersions = Object.fromEntries(
    activeResults
      .filter((result) => result.scoringBandVersion !== null)
      .map((result) => [result.criterionId, result.scoringBandVersion as number])
  );
  const blockingReasons = uniqueReasonCodes([
    ...activeResults.map((result) => result.reasonCode).filter((code): code is CustomerValueScoringCriterionReasonCode => code !== null),
    ...input.globalBlockingReasons
  ]);
  return {
    ready: blockingReasons.length === 0,
    blockingReasons,
    warnings: [],
    activeCriterionCount: activeResults.length,
    computableCriterionCount: activeResults.filter((result) => result.status === "computed").length,
    missingInputCriterionIds,
    missingScoringLogicCriterionIds,
    missingWeightCriterionIds,
    invalidWeightCriterionIds,
    ambiguousCriterionIds,
    unresolvedCriterionIds,
    segmentConfigurationValid: input.segmentConfigurationValid,
    modelVersion: input.modelVersion,
    weightingVersion: input.weightingVersion,
    scoringVersions
  };
}

function immutableJsonCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function calculateCustomerValueScore(input: CustomerValueScoreInput): CustomerValueScoreResult {
  const bandsByCriterionId = groupScoringBandsByCriterionId(input.confirmedScoringBands);
  const criterionResults: CustomerValueCriterionScoreResult[] = [];
  const globalBlockingReasons: CustomerValueScoringGlobalReasonCode[] = [];
  const modelInvalid = !isCustomerValueModelStorageType(input.model.modelType) || input.model.modelType === "unclassified";
  if (modelInvalid) globalBlockingReasons.push("invalid_model");

  const activeCriteria = input.criteria.filter((criterion) => criterion.active).sort((left, right) => left.id.localeCompare(right.id));
  if (activeCriteria.length === 0) globalBlockingReasons.push("no_active_criteria");

  let activeWeightTotal = 0;
  for (const criterion of input.criteria.slice().sort((left, right) => left.id.localeCompare(right.id))) {
    if (!criterion.active) {
      criterionResults.push({
        criterionId: criterion.id,
        rawValue: input.criterionValues[criterion.id] ?? null,
        normalizedValue: null,
        scoringBandId: null,
        scoringBandVersion: null,
        points: null,
        weight: null,
        weightedContribution: null,
        status: "ignored_inactive",
        reasonCode: null
      });
      continue;
    }

    const rawValue = input.criterionValues[criterion.id];
    const bandResolution = resolveCriterionBand(criterion, bandsByCriterionId[criterion.id] ?? [], rawValue, { modelStatus: input.model.status });
    const weightResolution = confirmedWeightForCriterion(criterion, input.confirmedWeights);
    if (!weightResolution.missing && !weightResolution.invalid && weightResolution.weight !== null) activeWeightTotal += weightResolution.weight;

    const baseResult: CustomerValueCriterionScoreResult = {
      criterionId: criterion.id,
      rawValue: rawValue ?? null,
      normalizedValue: bandResolution.normalizedValue,
      scoringBandId: bandResolution.status === "matched" ? bandResolution.bandId : null,
      scoringBandVersion: bandResolution.status === "matched" ? bandResolution.bandVersion : null,
      points: bandResolution.status === "matched" ? bandResolution.points : null,
      weight: weightResolution.weight,
      weightedContribution: null,
      status: "blocked",
      reasonCode: null
    };

    if (bandResolution.status === "missing_input") {
      criterionResults.push({ ...baseResult, reasonCode: "missing_input" });
      continue;
    }
    if (bandResolution.status === "missing_scoring_logic") {
      criterionResults.push({ ...baseResult, reasonCode: "missing_scoring_logic" });
      continue;
    }
    if (weightResolution.missing) {
      criterionResults.push({ ...baseResult, reasonCode: "missing_weight" });
      continue;
    }
    if (weightResolution.invalid || weightResolution.weight === null) {
      criterionResults.push({ ...baseResult, reasonCode: "invalid_weight" });
      continue;
    }
    if (bandResolution.status === "ambiguous") {
      criterionResults.push({ ...baseResult, reasonCode: "ambiguous_scoring_band" });
      continue;
    }
    if (bandResolution.status === "no_match") {
      criterionResults.push({ ...baseResult, reasonCode: "no_matching_scoring_band" });
      continue;
    }

    const normalizedScore = normalizeCriterionPoints(bandResolution.points);
    criterionResults.push({
      ...baseResult,
      normalizedValue: bandResolution.normalizedValue,
      points: bandResolution.points,
      weight: weightResolution.weight,
      weightedContribution: normalizedScore * (weightResolution.weight / 100),
      status: "computed",
      reasonCode: null
    });
  }

  if (activeCriteria.length > 0 && Math.abs(activeWeightTotal - 100) > EPSILON) {
    globalBlockingReasons.push("invalid_weight_sum");
  }

  const criterionBlocks = criterionResults.some((result) => result.status === "blocked");
  const canResolveScore = !modelInvalid && !criterionBlocks && !globalBlockingReasons.includes("no_active_criteria") && !globalBlockingReasons.includes("invalid_weight_sum");
  const rawScore = canResolveScore
    ? criterionResults.reduce((sum, result) => sum + (result.weightedContribution ?? 0), 0)
    : null;
  const roundedScore = rawScore === null ? null : roundCustomerValueScoreForOutput(rawScore);
  const segmentResolution = rawScore === null ? undefined : resolveCustomerValueSegment(rawScore, input.segments);
  if (segmentResolution?.status === "invalid_configuration") globalBlockingReasons.push("invalid_segment_configuration");
  if (segmentResolution?.status === "no_match") globalBlockingReasons.push("no_matching_segment");
  if (segmentResolution?.status === "ambiguous") globalBlockingReasons.push("ambiguous_segment");

  const readiness = deriveScoringReadiness({
    criterionResults,
    globalBlockingReasons,
    segmentConfigurationValid: !globalBlockingReasons.includes("invalid_segment_configuration"),
    modelVersion: input.modelVersion,
    weightingVersion: input.weightingVersion ?? null
  });
  const segmentId = segmentResolution?.status === "matched" ? segmentResolution.segmentId : null;
  const criterionIds = activeCriteria.map((criterion) => criterion.id);
  const computedResults = criterionResults.filter((result) => result.status === "computed");
  const scoringBandIds = computedResults.map((result) => result.scoringBandId).filter((id): id is string => id !== null);
  const scoringBandVersions = Object.fromEntries(
    computedResults
      .filter((result) => result.scoringBandId !== null && result.scoringBandVersion !== null)
      .map((result) => [result.scoringBandId as string, result.scoringBandVersion as number])
  );
  const snapshot: CustomerValueScoreSnapshot = immutableJsonCopy({
    workspaceId: input.model.workspaceId,
    modelId: input.model.modelId,
    modelVersion: input.modelVersion,
    engineVersion: CUSTOMER_VALUE_SCORING_ENGINE_VERSION,
    calculatedAt: input.calculatedAt,
    criterionValues: Object.fromEntries(criterionIds.map((criterionId) => [criterionId, input.criterionValues[criterionId] ?? null])),
    criterionIds,
    scoringBandIds,
    scoringBandVersions,
    points: Object.fromEntries(criterionResults.map((result) => [result.criterionId, result.points])),
    weights: Object.fromEntries(criterionResults.map((result) => [result.criterionId, result.weight])),
    weightingVersion: input.weightingVersion ?? null,
    weightedContributions: Object.fromEntries(criterionResults.map((result) => [result.criterionId, result.weightedContribution])),
    segmentDefinitionIds: input.segments.map((segment) => segment.id),
    segmentId,
    rawScore,
    roundedScore,
    scoreScale: CUSTOMER_VALUE_SCORE_SCALE
  });

  return {
    success: readiness.ready,
    readiness,
    modelId: input.model.modelId,
    modelVersion: input.modelVersion,
    engineVersion: CUSTOMER_VALUE_SCORING_ENGINE_VERSION,
    rawScore,
    roundedScore,
    scoreScale: CUSTOMER_VALUE_SCORE_SCALE,
    segmentId,
    segment: segmentResolution?.status === "matched" ? segmentResolution.segment : undefined,
    criterionResults: immutableJsonCopy(criterionResults),
    blockingReasons: readiness.blockingReasons,
    warnings: readiness.warnings,
    calculatedAt: input.calculatedAt,
    inputSnapshot: snapshot
  };
}

export function resolveCriterionScore(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[],
  rawValue: unknown
): CriterionScoreResolution {
  const resolution = resolveCriterionBand(criterion, bands, rawValue);
  if (resolution.status === "matched") {
    return { ok: true, points: resolution.points, normalizedScore: normalizeCriterionPoints(resolution.points), band: resolution.band, errors: [] };
  }
  const codeByReason: Record<CustomerValueScoringCriterionReasonCode, CustomerValueCriterionScoringErrorCode> = {
    missing_input: "missing_input",
    missing_scoring_logic: criterion.criterionType === "calculated" ? "calculated_formula_not_resolved" : "scoring_configuration_missing",
    missing_weight: "invalid_weight",
    invalid_weight: "invalid_weight",
    ambiguous_scoring_band: "scoring_band_overlap",
    no_matching_scoring_band: "criterion_value_unmatched",
    calculation_error: "criterion_value_unmatched"
  };
  return {
    ok: false,
    errors: [scoringError(codeByReason[resolution.reasonCode], resolution.status === "missing_scoring_logic" ? "bands" : "value", { criterionId: criterion.id })]
  };
}

export function resolveWeightedCustomerValueScore(input: WeightedCustomerValueScoreInput): WeightedCustomerValueScoreResolution {
  const result = calculateCustomerValueScore({
    model: {
      workspaceId: "compatibility_workspace",
      modelId: input.criteria[0]?.modelId ?? "compatibility_model",
      modelType: input.criteria[0]?.modelType ?? "existing_customer_value"
    },
    modelVersion: null,
    criteria: input.criteria,
    criterionValues: input.valuesByCriterionId,
    confirmedScoringBands: input.bandsByCriterionId,
    segments: [{ id: "compatibility_segment", name: "Compatibility", minScore: 0, maxScore: 100, minInclusive: true, maxInclusive: true }],
    weightingVersion: null,
    calculatedAt: "1970-01-01T00:00:00.000Z"
  });
  const contributions = result.criterionResults
    .filter((entry) => entry.status === "computed" && entry.points !== null && entry.weight !== null && entry.weightedContribution !== null)
    .map((entry) => ({
      criterionId: entry.criterionId,
      points: entry.points as number,
      normalizedScore: normalizeCriterionPoints(entry.points as number),
      weight: entry.weight as number,
      weightedContribution: entry.weightedContribution as number
    }));
  const errors = result.criterionResults.flatMap((entry): CustomerValueCriterionScoringError[] => {
    if (!entry.reasonCode) return [];
    const codeByReason: Record<CustomerValueScoringCriterionReasonCode, CustomerValueCriterionScoringErrorCode> = {
      missing_input: "missing_input",
      missing_scoring_logic: "scoring_configuration_missing",
      missing_weight: "invalid_weight",
      invalid_weight: "invalid_weight",
      ambiguous_scoring_band: "scoring_band_overlap",
      no_matching_scoring_band: "criterion_value_unmatched",
      calculation_error: "criterion_value_unmatched"
    };
    return [
      scoringError("weighted_score_missing_criterion_score", `valuesByCriterionId.${entry.criterionId}`, { criterionId: entry.criterionId }),
      scoringError(codeByReason[entry.reasonCode], `valuesByCriterionId.${entry.criterionId}`, { criterionId: entry.criterionId })
    ];
  });
  if (result.blockingReasons.includes("no_active_criteria")) errors.push(scoringError("weighted_score_no_active_criteria", "criteria"));
  if (result.blockingReasons.includes("invalid_weight_sum")) errors.push(scoringError("weighted_score_invalid_weight_total", "criteria.weight"));
  if (errors.length > 0) return { ok: false, contributions, errors };
  return {
    ok: true,
    finalScore: result.roundedScore ?? undefined,
    contributions,
    errors: []
  };
}

export function validateCustomerValueCrmMappings(
  mappings: readonly CustomerValueCriterionCrmMappingContract[]
): CriterionScoringValidationResult {
  const errors: CustomerValueCriterionScoringError[] = [];
  const activePrimaryKeys = new Set<string>();
  for (const [index, mapping] of mappings.entries()) {
    if (!mapping.workspaceId || !mapping.modelId || !mapping.criterionId || !mapping.crmFieldName.trim()) {
      errors.push(scoringError("invalid_crm_mapping", `mappings.${index}`));
      continue;
    }
    if (mapping.active && mapping.primary) {
      const key = [mapping.workspaceId, mapping.modelId, mapping.criterionId, mapping.crmProvider, mapping.crmObjectType].join(":");
      if (activePrimaryKeys.has(key)) {
        errors.push(scoringError("duplicate_primary_crm_mapping", `mappings.${index}`, { criterionId: mapping.criterionId }));
      }
      activePrimaryKeys.add(key);
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateActiveCriteriaWeightTotal(criteria: readonly Pick<CustomerValueCriterionDefinition, "id" | "weight" | "active">[]): CustomerValueCriteriaWeightValidation {
  const totalWeight = criteria
    .filter((criterion) => criterion.active)
    .reduce((sum, criterion) => sum + criterion.weight, 0);
  const errors = Math.abs(totalWeight - 100) <= EPSILON
    ? []
    : [scoringError("weighted_score_invalid_weight_total", "criteria.weight", { totalWeight })];
  return { ok: errors.length === 0, totalWeight, errors };
}

export function resolveCustomerValueScoringConfigurationDraftStatus(totalWeight: number): CustomerValueScoringConfigurationDraftStatus {
  if (Math.abs(totalWeight - 100) <= EPSILON) return "complete";
  return totalWeight < 100 ? "incomplete_underweighted" : "incomplete_overweighted";
}
